import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, LogOut, Trash2, Rocket } from "lucide-react";

export default function TemplatesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(null);
  const [useTpl, setUseTpl] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", startDate: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/templates").then((r) => setTemplates(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const createProject = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/projects/from-template", { templateId: useTpl.id, ...form });
      toast.success("Project created from template");
      navigate(`/projects/${data.id}/dashboard`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const remove = async (t) => {
    try { await api.delete(`/templates/${t.id}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/projects" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Projects
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="font-medium">{user?.name}</span>
            <button data-testid="logout-btn" onClick={logout} className="p-1.5 hover:text-red-600"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8" data-testid="templates-page">
        <PageHeader overline={`${user?.tenantName} · Reusable project blueprints`} title="Template Library">
          <div className="text-xs text-muted-foreground mt-1">Save any project as a template from its Toolkit Manager page — plan structure, dependencies and toolkit presets are captured.</div>
        </PageHeader>

        {templates === null ? <div className="text-muted-foreground mt-4">Loading…</div>
          : templates.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center text-muted-foreground mt-4">
              No templates yet. Open a project → Settings → Toolkit Manager → "Save as template".
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {templates.map((t) => (
                <div key={t.id} data-testid="template-card" className="border border-border bg-card p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-display font-bold text-lg tracking-tight">{t.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{t.description || `Captured from ${t.sourceProject || "a project"}`}</p>
                    </div>
                    <button data-testid="template-delete-btn" onClick={() => remove(t)} className="text-muted-foreground hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <StatusBadge value={t.methodology === "Pmbok" ? "PMBOK" : t.methodology} tone="blue" />
                    <span className="text-xs text-muted-foreground">{t.taskCount} tasks · {(t.enabledTools || []).filter((x) => x.isEnabled).length} tools enabled</span>
                  </div>
                  <Button data-testid="template-use-btn" className="rounded-none w-full mt-4 bg-[#1E6FE8] hover:bg-[#1557C6]"
                    onClick={() => { setUseTpl(t); setForm({ name: "", code: "", startDate: new Date().toISOString().slice(0, 10) }); }}>
                    <Rocket className="h-4 w-4 mr-1.5" /> New project from template
                  </Button>
                </div>
              ))}
            </div>
          )}
      </main>

      <Dialog open={!!useTpl} onOpenChange={(o) => !o && setUseTpl(null)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-display">New project — {useTpl?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Project name</Label><Input data-testid="tpl-project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code</Label><Input data-testid="tpl-project-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
              <div><Label>Start date</Label><Input data-testid="tpl-project-start-input" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></div>
            </div>
            <p className="text-xs text-muted-foreground">All task dates are laid out relative to the start date; dependencies and toolkit presets come with it.</p>
            <Button data-testid="tpl-create-project-btn" className="w-full rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={createProject} disabled={busy || !form.name}>
              {busy ? "Creating…" : "Create project"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
